import { ReactNode } from 'react';
import {
    MdDownload,
    MdOutlineFileOpen,
    MdOutlineFindInPage,
    MdOutlineRateReview,
    MdOutlineReplay,
    MdOutlineVerified,
} from 'react-icons/md';
import { IOffer, IOfferAction, OfferActionTypes } from '@models/winnerSelection/ITableDemand';
import { IListElement } from '@srm/ui/types/IListElement';
import { RouteNames } from '@/router/router.types';
import { OfferModalKey } from '../hooks/useOfferModals';
import { downloadOfferAttachments } from '../OfferAttachmentsArchive';

const menuLabel = (text: string, icon: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 'var(--lh-tight)' }}>
        {icon}
        <span>{text}</span>
    </div>
);

/**
 * Строит массив пунктов меню kebab по списку actions из offer.actions.
 * Раньше — 100-строчный switch в компоненте. Теперь — чистая функция,
 * принимает open-callback (из useOfferModals) и tableKey.
 *
 * Для каждого action.type выбирается:
 * - какую модалку открыть → open(modalKey, offer, action);
 * - либо чем заменить (download через FilesService или окно архива в фоне / window.open).
 *
 * Action с visible=false — исключается (кроме OpenOffer / ApproveToStageTwo, где
 * пункт остаётся, но disabled).
 *
 * `allowedTypes` сужает набор поддерживаемых типов (KO-таблица) — один builder
 * на оба меню, чтобы правки endpoint'ов/роутов не расходились по копиям.
 */
export function buildOfferMenuActions(
    offer: IOffer,
    tableKey: string,
    open: (modal: OfferModalKey, offer: IOffer, action?: IOfferAction) => void,
    allowedTypes?: ReadonlySet<OfferActionTypes>,
): IListElement[] {
    return offer.actions
        .map((action): IListElement | null => {
            if (allowedTypes && !allowedTypes.has(OfferActionTypes[action.type])) return null;
            switch (OfferActionTypes[action.type]) {
                case OfferActionTypes.CustomForm:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(
                            action?.displayName ?? 'Просмотреть предложение',
                            <MdOutlineFindInPage size="20px" />,
                        ),
                        handler: () => open('customForm', offer, action),
                    };
                case OfferActionTypes.DownloadAttachments:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(action.displayName ?? 'Скачать документацию', <MdDownload size="20px" />),
                        handler: () =>
                            downloadOfferAttachments(offer.key, tableKey, action, () =>
                                open('attachmentsArchive', offer, action),
                            ),
                    };
                case OfferActionTypes.OpenOffer:
                    return {
                        label: menuLabel(
                            action?.displayName ?? 'Открыть предложение',
                            <MdOutlineFileOpen size="20px" />,
                        ),
                        handler: () => {
                            window.open(`.${RouteNames.DOCUMENT.replace(':id', offer.key)}`, '_blank');
                        },
                        disabled: !action.visible,
                    };
                case OfferActionTypes.TechAnalysisOffer:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(
                            action?.displayName ?? 'Технический анализ',
                            <MdOutlineRateReview size="20px" />,
                        ),
                        handler: () => open('techAnalysis', offer, action),
                    };
                case OfferActionTypes.RateOffer:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(action?.displayName ?? 'Оценить', <MdOutlineRateReview size="20px" />),
                        handler: () => open('rate', offer, action),
                        disabled: !action.visible,
                    };
                case OfferActionTypes.ApproveToStageTwo:
                    return {
                        label: menuLabel(
                            action?.displayName ?? 'Допустить ко второму этапу',
                            <MdOutlineVerified size="20px" />,
                        ),
                        handler: () => open('approveToStageTwo', offer, action),
                        disabled: !action.visible,
                    };
                case OfferActionTypes.Revaluate:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(
                            action?.displayName ?? 'Отправить на переоценку',
                            <MdOutlineReplay size="20px" />,
                        ),
                        handler: () => open('revaluate', offer, action),
                    };
                case OfferActionTypes.RateOfferPKO:
                    if (!action.visible) return null;
                    return {
                        label: menuLabel(action?.displayName ?? 'Оценить', <MdOutlineRateReview size="20px" />),
                        handler: () => open('ratePko', offer, action),
                        disabled: !action.visible,
                    };
                default:
                    return null;
            }
        })
        .filter((item): item is IListElement => item !== null);
}
